import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "sushiweb/Modal";
import Button from "sushiweb/Button/Button";

import Text from "../../shared/text/Text";
import BODY_TYPE_REGISTRY from "./bodyRegistry";
import { handleActionType } from "../../../utils/helpers";
import { setErrorToast } from "../../../redux/dispatchers/userDispatcher";
import { closeModal } from "../../../redux/dispatchers/modalDispatcher";
import { getClaimOwnershipDeviceId } from "../../../utils/claimOwnershipDeviceId";
import { collectErrors, isBodyValid } from "./validators";
import { isBodyReady } from "./utils";
import styles from "./ClaimOwnershipModal.module.scss";
import ZIcon from "../../shared/ZIcon/ZIcon";
import { isEmpty } from "lodash-es";
import { HEADER_KEYS } from "../../../utils/constants/others";
import { sendEventToJumbo } from "../../../tracking/jumbo";
import { ImpressionTracker } from "../../../tracking/ImpressionTracker";
import { parseImpressionTrackingData } from "../../../tracking/utils";
import { TRACKING_ACTIONS, TRACKING_TABLES } from "../../../tracking/constants";

const BUTTON_APPEARANCE_BY_VARIANT = {
    filled: "solid",
    solid: "solid",
    bordered: "outline",
    outline: "outline",
    ghost: "link",
    text: "link",
    link: "link",
};

const SUSHI_BTN_COLORS = new Set([
    "red",
    "green",
    "blue",
    "yellow",
    "purple",
    "lime",
    "coal",
    "chalk",
]);

const indexErrorsByField = (errors = []) =>
    errors.reduce((acc, item) => {
        if (item?.field && item?.error) acc[item.field] = item.error;
        return acc;
    }, {});

const ButtonContentWithLoader = ({ loading, children }) => (
    <span className={styles.btnContent}>
        <span
            className={`${styles.btnLabel} ${loading ? styles.btnLabelHidden : ""}`}
            aria-hidden={loading}
        >
            {children}
        </span>
        <span
            className={`${styles.btnSpinnerWrap} ${loading ? styles.btnSpinnerWrapVisible : ""}`}
            aria-hidden={!loading}
        >
            <span
                className={styles.btnSpinner}
                role="status"
                aria-label="Loading"
            />
        </span>
    </span>
);

const renderHeader = ({ header, onClose, bodyType }) => {
    if (isEmpty(header)) return null;

    const { imageUrl, title, showCloseBtn } = header ?? {};
    if (!imageUrl && !title && !showCloseBtn) return null;
    const imageSizeClass =
        bodyType === "SUCCESS"
            ? styles.headerImageFull
            : styles.headerImageCompact;
    const isCloseOnly = showCloseBtn && !imageUrl && !title;

    return (
        <div
            className={`${styles.header} ${header.imageUrl ? styles.headerWithImage : ""} ${header.imageUrl ? imageSizeClass : ""} ${showCloseBtn && header.imageUrl ? styles.headerWithClose : ""} ${isCloseOnly ? styles.headerCloseOnly : ""}`}
        >
            {imageUrl ? <img src={imageUrl} alt="" /> : null}
            {title && <Text uiData={title} className={styles.headerTitle} />}
            {showCloseBtn && (
                <ZIcon
                    unicode="E921"
                    className={styles.headerIcon}
                    onClick={onClose}
                />
            )}
        </div>
    );
};

const ClaimOwnershipModal = ({ visible, onClose, data }) => {
    const [modalData, setModalData] = useState(data);
    const [bodyState, setBodyState] = useState({});
    const [errorPatch, setErrorPatch] = useState(null);
    const [loadingIdx, setLoadingIdx] = useState(null);
    const dispatch = useDispatch();
    const userData = useSelector((state) => state.user?.data);

    const trackClick = useCallback(
        (trackingParams) => {
            if (!trackingParams?.element) return;
            sendEventToJumbo(
                TRACKING_TABLES.HYPERPURE_CLICKS,
                { ...trackingParams },
                userData,
            );
        },
        [userData],
    );

    const trackImpression = useCallback(
        (payload) =>
            sendEventToJumbo(
                TRACKING_TABLES.HYPERPURE_IMPRESSION,
                payload,
                userData,
            ),
        [userData],
    );

    const trackEndpointOutcome = useCallback(
        (trackingParams, response, hasNetworkError = false) => {
            if (!trackingParams) return;
            const isModalError = response?.action?.type === "MODAL_ERROR";
            trackClick({
                ...trackingParams,
                action: TRACKING_ACTIONS.ACTION_TYPE_UPDATE,
                element:
                    hasNetworkError || isModalError
                        ? "claim_ownership_step_error"
                        : "claim_ownership_step_success",
            });
        },
        [trackClick],
    );

    useEffect(() => {
        setModalData(data);
        setBodyState({});
        setErrorPatch(null);
        setLoadingIdx(null);
    }, [data]);

    const applyAction = useCallback((resp) => {
        const action = resp?.action;
        if (!action) return;

        if (action.type === "UPDATE_MODAL") {
            setModalData(action.data);
            setBodyState({});
            setErrorPatch(null);
            setLoadingIdx(null);
        } else if (action.type === "MODAL_ERROR") {
            setErrorPatch(action.data ?? null);
        }
    }, []);

    const setBodyStateWithErrorClear = useCallback((updater) => {
        setBodyState(updater);
        setErrorPatch(null);
    }, []);

    const patchedErrors = indexErrorsByField(errorPatch?.errors);
    const allErrors = {
        ...collectErrors(modalData?.body, bodyState),
        ...patchedErrors,
    };
    const patchedDisabledFields = new Set(errorPatch?.disabledFields ?? []);

    const isFooterDisabled =
        !isBodyValid(modalData?.body, bodyState) ||
        !isBodyReady(modalData?.body, bodyState) ||
        Object.keys(patchedErrors).length > 0 ||
        patchedDisabledFields.size > 0;

    const dismissModal = useCallback(
        () => dispatch(closeModal(modalData?.modalConfig?.id)),
        [dispatch, modalData],
    );

    const handleDismiss = useCallback(() => {
        trackClick(modalData?.header?.trackingParams);
        onClose?.();
    }, [modalData?.header?.trackingParams, onClose, trackClick]);

    const trackClickRef = useRef(trackClick);
    trackClickRef.current = trackClick;

    const searchTrackingParams = modalData?.body?.searchConfig?.trackingParams;
    const handleSearch = useCallback(
        () => trackClickRef.current(searchTrackingParams),
        [searchTrackingParams],
    );

    const buildClaimOwnershipHeaders = () => {
        const xAppDeviceId = getClaimOwnershipDeviceId();
        return xAppDeviceId
            ? { [HEADER_KEYS.X_APP_DEVICE_ID_HEADER]: xAppDeviceId }
            : undefined;
    };

    const dispatchAction = useCallback(
        (action, trackingParams) => {
            if (!action) return;
            const { type, data: actionData } = action;
            trackClick(trackingParams);

            handleActionType(type, actionData, undefined, {
                params: bodyState,
                body: bodyState,
                headers: buildClaimOwnershipHeaders(),
                SuccessCallback: (response) => {
                    trackEndpointOutcome(trackingParams, response);
                    applyAction(response);
                },
                ErrCallback: () =>
                    trackEndpointOutcome(trackingParams, null, true),
                dismissModal,
            });
        },
        [
            applyAction,
            bodyState,
            dismissModal,
            trackClick,
            trackEndpointOutcome,
        ],
    );

    const handleButtonClick = useCallback(
        (button, idx) => {
            const { action, trackingParams } = button ?? {};
            if (!action || isFooterDisabled || loadingIdx !== null) return;
            const isEndpoint = action?.type === "ENDPOINT";
            trackClick(trackingParams);
            if (isEndpoint) setLoadingIdx(idx);
            handleActionType(action.type, action.data, undefined, {
                params: bodyState,
                body: bodyState,
                headers: buildClaimOwnershipHeaders(),
                SuccessCallback: (resp) => {
                    if (isEndpoint) {
                        setLoadingIdx(null);
                        trackEndpointOutcome(trackingParams, resp);
                    }
                    applyAction(resp);
                },
                ErrCallback: (err) => {
                    if (isEndpoint) {
                        setLoadingIdx(null);
                        trackEndpointOutcome(trackingParams, null, true);
                    }
                    const message =
                        err?.response?.data?.error?.message ||
                        "Something went wrong. Please try again.";
                    dispatch(setErrorToast(message));
                },
                dismissModal,
            });
        },
        [
            applyAction,
            bodyState,
            dispatch,
            isFooterDisabled,
            loadingIdx,
            dismissModal,
            trackClick,
            trackEndpointOutcome,
        ],
    );

    const renderBody = (body) => {
        const { type, imageUrl } = body ?? {};

        const Component = BODY_TYPE_REGISTRY[type];
        if (!Component) return null;
        const metaData = parseImpressionTrackingData(
            body?.impressionTrackingData,
        );
        return (
            <ImpressionTracker
                key={metaData?.TrackingId}
                metaData={metaData}
                eventHandler={trackImpression}
                className={styles.bodyImpressionWrapper}
            >
                <div
                    className={`${styles.body} ${imageUrl ? (type === "SUCCESS" ? styles.bodyWithImage2 : styles.bodyWithImage) : ""}`}
                >
                    <Component
                        body={body}
                        state={bodyState}
                        setState={setBodyStateWithErrorClear}
                        dispatchAction={dispatchAction}
                        onItemSelect={trackClick}
                        onSearch={handleSearch}
                        errors={allErrors}
                        disabledFields={patchedDisabledFields}
                    />
                </div>
            </ImpressionTracker>
        );
    };

    const renderFooter = (footer) => {
        if (!footer?.buttonConfig?.length) return null;
        return (
            <div className={styles.footer}>
                {footer.buttonConfig.map((btn, idx) => {
                    const { mainContent, variant, theme } = btn ?? {};
                    const isLoading = loadingIdx === idx;
                    const appearance =
                        BUTTON_APPEARANCE_BY_VARIANT[variant] ?? "solid";
                    const isTextBtn = appearance === "link";
                    return (
                        <Button
                            key={idx}
                            btnColor={
                                SUSHI_BTN_COLORS.has(theme) ? theme : "red"
                            }
                            appearance={appearance}
                            fullWidth
                            borderRadius="12px"
                            disabled={isFooterDisabled || loadingIdx !== null}
                            onClick={() => handleButtonClick(btn, idx)}
                            className={`${styles.footerBtn} ${isTextBtn ? styles.footerBtnText : ""} ${
                                isFooterDisabled && !isTextBtn
                                    ? styles.disabled
                                    : ""
                            }`}
                        >
                            <ButtonContentWithLoader loading={isLoading}>
                                <Text uiData={mainContent?.title} />
                            </ButtonContentWithLoader>
                        </Button>
                    );
                })}
            </div>
        );
    };

    const renderBanner = (banner) => {
        if (!banner?.title) return null;
        const variantClass =
            banner.variant === "error"
                ? styles.bannerError
                : banner.variant === "info"
                  ? styles.bannerInfo
                  : styles.bannerWarning;
        const metaData = parseImpressionTrackingData(
            banner?.impressionTrackingData,
        );
        return (
            <ImpressionTracker
                key={metaData?.TrackingId}
                metaData={metaData}
                eventHandler={trackImpression}
            >
                <div className={`${styles.banner} ${variantClass}`}>
                    {banner.icon ? (
                        <ZIcon
                            unicode={banner.icon}
                            className={styles.bannerIcon}
                        />
                    ) : null}
                    <Text uiData={banner.title} className={styles.bannerText} />
                </div>
            </ImpressionTracker>
        );
    };

    const { modalConfig, header, body, footer } = modalData ?? {};
    const banner = errorPatch?.banner ?? modalData?.banner;
    const { closeOnOutsideClick = true } = modalConfig ?? {};

    return (
        <Modal
            visible={visible}
            onClose={handleDismiss}
            overlayClickClose={closeOnOutsideClick}
            isCentered
            borderRadius="24px"
            type="midScreenSize"
            className={styles.modalRoot}
        >
            {modalData && (
                <div
                    className={`${styles.container} ${
                        header?.imageUrl ? styles.containerHero : ""
                    }`}
                >
                    {renderHeader({
                        header,
                        onClose: handleDismiss,
                        bodyType: body?.type,
                    })}
                    {renderBody(body)}
                    <div>
                        {renderBanner(banner)}
                        {renderFooter(footer)}
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default ClaimOwnershipModal;
